import { useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

export default function TrueFalseExercise({
  exercise,
  submittingLearningExercise,
  submitLearningExercise,
  styles,
}) {
  const { language } = useLanguage();

  // =========================================================
  // Canonical data
  // These remain the source of truth for validation.
  // =========================================================
  const statement =
    exercise.exercise_data?.statement ||
    exercise.prompt ||
    "";

  const correctAnswer =
    exercise.exercise_data?.correct_answer;

  // =========================================================
  // Display-only ES / RU translations
  // =========================================================
  const translatedData =
    exercise.translated_exercise_data || {};

  const translatedPrompt =
    language === "es" || language === "ru"
      ? exercise.translated_prompt || null
      : null;

  const translatedStatement =
    language === "es" || language === "ru"
      ? translatedData.statement || null
      : null;

  // =========================================================
  // Separate legacy mixed English + Chinese display text
  // Example:
  // "Choose True or False. 请选择正确或错误。"
  // =========================================================
  const splitEnglishChinese = (text = "") => {
    const firstChineseIndex = text.search(
      /[\u3400-\u4DBF\u4E00-\u9FFF]/
    );

    if (firstChineseIndex === -1) {
      return {
        english: text.trim(),
        chinese: null,
      };
    }

    return {
      english: text
        .slice(0, firstChineseIndex)
        .trim(),

      chinese: text
        .slice(firstChineseIndex)
        .trim(),
    };
  };

  const promptParts =
    splitEnglishChinese(exercise.prompt || "");

  const statementParts =
    splitEnglishChinese(statement);

  const englishPrompt = promptParts.english;
  const chinesePrompt = promptParts.chinese;

  const englishStatement = statementParts.english;
  const chineseStatement = statementParts.chinese;

  // =========================================================
  // State
  // =========================================================
  const [selectedAnswer, setSelectedAnswer] =
    useState(null);

  const [submitted, setSubmitted] =
    useState(false);

  // =========================================================
  // Submit
  // Validation remains 100% canonical boolean.
  // =========================================================
  const handleSubmit = async () => {
    if (selectedAnswer === null) return;

    const isCorrect =
      selectedAnswer === correctAnswer;

    const success =
      await submitLearningExercise({
        exerciseId: exercise.id,
        answer: {
          selected_answer: selectedAnswer,
        },
        isCorrect,
      });

    if (!success) return;

    setSubmitted(true);
  };

  const handleTryAgain = () => {
    setSelectedAnswer(null);
    setSubmitted(false);
  };

  return (
    <div
      style={{
        ...styles.emptyBox,
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        True / False
      </div>

      {/* =====================================================
          Prompt
          EN: English only
          ZH: English + Chinese
          ES/RU: English + selected translation
      ====================================================== */}

      {englishPrompt && (
        <div style={styles.quizPath}>
          {englishPrompt}
        </div>
      )}

      {language === "zh" && chinesePrompt && (
        <div
          style={{
            ...styles.quizPath,
            marginTop: "4px",
            color: "#6b7280",
          }}
        >
          {chinesePrompt}
        </div>
      )}

      {(language === "es" ||
        language === "ru") &&
        translatedPrompt &&
        translatedPrompt !==
          translatedStatement && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {translatedPrompt}
          </div>
        )}

      {/* =====================================================
          Statement
      ====================================================== */}

      {englishStatement &&
        englishStatement !== englishPrompt && (
          <div
            style={{
              marginTop: "14px",
              fontSize: "17px",
              lineHeight: "1.6",
            }}
          >
            {englishStatement}
          </div>
        )}

      {language === "zh" &&
        chineseStatement && (
          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              lineHeight: "1.6",
            }}
          >
            {chineseStatement}
          </div>
        )}

      {(language === "es" ||
        language === "ru") &&
        translatedStatement && (
          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              lineHeight: "1.6",
            }}
          >
            {translatedStatement}
          </div>
        )}

      {/* =====================================================
          True / False buttons
          Values remain canonical booleans.
      ====================================================== */}

      <div
        style={{
          display: "flex",
          gap: "10px",
          marginTop: "16px",
          flexWrap: "wrap",
        }}
      >
        <button
          style={
            selectedAnswer === true
              ? styles.smallPrimaryButton
              : styles.smallSecondaryButton
          }
          disabled={submitted}
          onClick={() =>
            setSelectedAnswer(true)
          }
        >
          True
        </button>

        <button
          style={
            selectedAnswer === false
              ? styles.smallPrimaryButton
              : styles.smallSecondaryButton
          }
          disabled={submitted}
          onClick={() =>
            setSelectedAnswer(false)
          }
        >
          False
        </button>
      </div>

      {/* =====================================================
          Submit / Result
      ====================================================== */}

      {!submitted ? (
        <button
          style={{
            ...styles.smallPrimaryButton,
            marginTop: "16px",
          }}
          disabled={
            submittingLearningExercise ||
            selectedAnswer === null
          }
          onClick={handleSubmit}
        >
          {submittingLearningExercise
            ? "Submitting..."
            : "Submit Answer"}
        </button>
      ) : (
        <div style={{ marginTop: "16px" }}>
          {selectedAnswer ===
          correctAnswer ? (
            <div
              style={{
                color: "#16a34a",
                fontWeight: "bold",
              }}
            >
              Correct
            </div>
          ) : (
            <div
              style={{
                color: "#dc2626",
                fontWeight: "bold",
              }}
            >
              Incorrect · Correct answer:{" "}
              {correctAnswer
                ? "True"
                : "False"}
            </div>
          )}

          <button
            style={{
              ...styles.smallSecondaryButton,
              marginTop: "10px",
            }}
            onClick={handleTryAgain}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}